/**
 * Account gate (decisions 15–16, fair-api `docs/release_journal/acct.md`) — the two answers of
 * the bearer that end a session on this device:
 *  - 401 `{"detail": "token_revoked"}`: the password was changed elsewhere; the token is dead
 *    and so is the refresh token (Cognito global sign-out) → sign out locally, no refresh.
 *  - 401 `{"detail": "user_not_provisioned"}` (2026-10-21, order D; decision 8.3): a valid token whose
 *    account is genuinely invalid (purged, unknown or disabled in the user pool) → sign out locally,
 *    no retry, the reason on the sign-in screen.
 *  - NOT a gate: 503 `{"detail": "provisioning_unavailable"}` + `Retry-After` (decision 8.3) — the
 *    server could not open the account right now. The interceptor retries (bounded, honouring
 *    `Retry-After`, `src/api/interceptors/provisioningRetry.ts`), then the session host says
 *    "Try again". Never a sign-out.
 *  - 403 `{"detail": {"code": "account_pending_deletion", "purge_at", "restore_available",
 *    "restore_available_until", "restore_token"?}}` on any bearer call AND on `POST /auth/login`
 *    → the restore screen (fair-api `FE_CONTRACT_CHANGES.md`, 2026-10-21, decision 4). The code
 *    was `account_scheduled_for_deletion` until then — still accepted for one release.
 * The case transformer camelises error bodies outside `auth/`, so both spellings are read.
 */

export type AccountGate =
  | { kind: 'token_revoked' }
  | { kind: 'user_not_provisioned' }
  | { kind: 'scheduled_for_deletion'; restoreAvailableUntil: string | null }

export const ACCOUNT_TOKEN_REVOKED = 'token_revoked'
export const ACCOUNT_USER_NOT_PROVISIONED = 'user_not_provisioned'
export const ACCOUNT_PENDING_DELETION = 'account_pending_deletion'
/** The code before 2026-10-21 (decision 4 renamed it) — accepted for one release, then drop. */
export const ACCOUNT_SCHEDULED_FOR_DELETION = 'account_scheduled_for_deletion'
const ACCOUNT_DELETION_CODES: readonly unknown[] = [ACCOUNT_PENDING_DELETION, ACCOUNT_SCHEDULED_FOR_DELETION]

function detailOf(data: unknown): unknown {
  return data && typeof data === 'object' ? (data as { detail?: unknown }).detail : undefined
}

/** The gate an error response names, or `null` for every other refusal. */
export function parseAccountGate(status: number | undefined, data: unknown): AccountGate | null {
  const detail = detailOf(data)
  if (status === 401 && detail === ACCOUNT_TOKEN_REVOKED) return { kind: 'token_revoked' }
  if (status === 401 && detail === ACCOUNT_USER_NOT_PROVISIONED) return { kind: 'user_not_provisioned' }
  if (status === 403 && detail && typeof detail === 'object') {
    const body = detail as Record<string, unknown>
    if (!ACCOUNT_DELETION_CODES.includes(body.code)) return null
    const until = body.restore_available_until ?? body.restoreAvailableUntil
    return { kind: 'scheduled_for_deletion', restoreAvailableUntil: typeof until === 'string' && until ? until : null }
  }
  return null
}

/** Same, from an axios error. */
export function accountGateOfError(error: unknown): AccountGate | null {
  const response = (error as { response?: { status?: number; data?: unknown } } | null)?.response
  return parseAccountGate(response?.status, response?.data)
}

/**
 * A server date (`2026-11-19T00:32:18+00:00`, or the naive `recoveryDeadline` of `DELETE /user`,
 * which is UTC) → `Date`, `null` when unreadable.
 */
export function parseAccountDeadline(value: string | null | undefined): Date | null {
  if (!value) return null
  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value)
  const date = new Date(hasZone ? value : `${value}Z`)
  return Number.isNaN(date.getTime()) ? null : date
}

/** "19 November 2026" in the UI language; empty when the date is unreadable. */
export function formatAccountDeadline(value: string | null | undefined, locale: string): string {
  const date = parseAccountDeadline(value)
  if (!date) return ''
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date)
}

/** `POST /account/restore` refusals (acct journal § FE contract 16), plus `unknown`. */
export type AccountRestoreError =
  | 'invalid_restore_token'
  | 'restore_expired'
  | 'restore_not_available'
  | 'restore_not_configured'
  | 'unknown'

const RESTORE_ERRORS_BY_STATUS: Record<number, AccountRestoreError> = {
  401: 'invalid_restore_token',
  410: 'restore_expired',
  409: 'restore_not_available',
  503: 'restore_not_configured',
}

export function accountRestoreErrorOf(error: unknown): AccountRestoreError {
  const response = (error as { response?: { status?: number; data?: unknown } } | null)?.response
  const detail = detailOf(response?.data)
  const known: readonly AccountRestoreError[] = [
    'invalid_restore_token',
    'restore_expired',
    'restore_not_available',
    'restore_not_configured',
  ]
  if (typeof detail === 'string' && (known as readonly string[]).includes(detail)) return detail as AccountRestoreError
  return (response?.status !== undefined && RESTORE_ERRORS_BY_STATUS[response.status]) || 'unknown'
}

/**
 * Decisions 8.1.2 / 8.1.5: what the screen after `DELETE /user` does — `cancel` (offer "Cancel" with
 * the answer's `restore_token`) or `sign_out` (no token: the server signs no links; the old flow).
 */
export function afterDeletion(response: { restoreToken?: string | null; recoveryDeadline?: string } | null | undefined):
  | { kind: 'cancel'; token: string; deadline: string }
  | { kind: 'sign_out' } {
  const token = response?.restoreToken?.trim()
  if (!token) return { kind: 'sign_out' }
  return { kind: 'cancel', token, deadline: response?.recoveryDeadline ?? '' }
}

/** A 403 `account_pending_deletion` while the cancel screen is open does not end this session. */
export function keepsSessionOnGate(gate: AccountGate, cancelWindowOpen: boolean): boolean {
  return cancelWindowOpen && gate.kind === 'scheduled_for_deletion'
}
